import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2, PieChart, Coins } from 'lucide-react';
import { useAuth, formatApiError } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${(n || 0).toLocaleString()}`;

export default function Fractional() {
  const { user, authHeaders } = useAuth();
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/fractional/offerings`).then((r) => setOfferings(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <PieChart size={14} strokeWidth={1.5} /> Fractional Investment Engine
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Own a slice<br />
            <span className="font-display italic text-[#D4AF37]">of the corridor.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            Purchase Sovereign Shares of curated estates. Quarterly dividends distributed
            pro-rata under the 75/15/10 Lineage Law. Settlement is corridor-aware.
          </p>
        </div>
      </div>

      {user && (
        <div className="mb-8">
          <Link to="/fractional/portfolio" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] hover:text-white transition-colors">
            View Your Sovereign Portfolio <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      )}

      {loading ? (
        <div className="text-center py-24"><Loader2 size={20} className="animate-spin text-[#D4AF37] mx-auto" /></div>
      ) : offerings.length === 0 ? (
        <div className="sov-border bg-[#0A0A0A] p-16 text-center">
          <Coins size={28} className="text-[#D4AF37] mx-auto mb-6" strokeWidth={1} />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">No Open Offerings</div>
          <p className="text-sm text-[#A3A3A3] font-light max-w-md mx-auto">
            Mercy is calibrating the next tranche. Subscribe via the Wingman to be notified.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {offerings.map((o) => <OfferingCard key={o.id} offering={o} user={user} authHeaders={authHeaders} />)}
        </div>
      )}
    </div>
  );
}

const OfferingCard = ({ offering, user, authHeaders }) => {
  const [shares, setShares] = useState(1);
  const [buying, setBuying] = useState(false);
  const available = offering.total_shares - offering.shares_sold;
  const filledPct = (offering.shares_sold / offering.total_shares) * 100;
  const cost = (offering.share_price_usd * shares).toFixed(2);

  const buy = async () => {
    if (!user) { window.location.href = '/login'; return; }
    setBuying(true);
    try {
      await axios.post(`${API}/fractional/purchase`,
        { offering_id: offering.id, shares: Number(shares) },
        { headers: authHeaders(), withCredentials: true });
      toast.success(`Acquired ${shares} sovereign shares.`);
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Acquisition refused.');
    } finally { setBuying(false); }
  };

  return (
    <div data-testid={`offering-${offering.id}`} className="sov-border bg-[#0A0A0A] overflow-hidden flex flex-col">
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={offering.property_image} alt={offering.property_title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
        <div className="absolute top-4 left-4">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 bg-black/40 backdrop-blur-sm">
            {offering.country}
          </span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-xl md:text-2xl font-light text-white mb-1">{offering.property_title}</h3>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">{offering.location}</div>
        </div>
      </div>

      <div className="p-6 md:p-8 flex-1 flex flex-col">
        <div className="grid grid-cols-3 gap-4 mb-6 border-b border-[#D4AF37]/15 pb-6">
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Share Price</div>
            <div className="font-display text-xl text-[#D4AF37]">{fmt$(offering.share_price_usd)}</div>
          </div>
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Total Valuation</div>
            <div className="font-display text-xl text-[#F5F5F5]">{fmt$(offering.total_valuation_usd)}</div>
          </div>
          <div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Projected Yield</div>
            <div className="font-display text-xl text-[#F5F5F5]">{offering.projected_annual_yield_pct}%</div>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex justify-between text-[10px] tracking-[0.3em] uppercase mb-2">
            <span className="text-[#A3A3A3]">{offering.shares_sold} / {offering.total_shares} shares</span>
            <span className="text-[#D4AF37]">{filledPct.toFixed(1)}% funded</span>
          </div>
          <div className="h-[3px] bg-[#1a1a1a] overflow-hidden">
            <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${filledPct}%` }}></div>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <input
            type="number"
            min="1"
            max={available}
            value={shares}
            onChange={(e) => setShares(e.target.value)}
            data-testid={`shares-input-${offering.id}`}
            className="input-sovereign flex-1"
          />
          <div className="text-right">
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3]">Total</div>
            <div className="font-display text-lg text-[#D4AF37]">${cost}</div>
          </div>
        </div>

        <button onClick={buy} disabled={buying || available === 0} data-testid={`buy-shares-${offering.id}`} className="btn-sovereign w-full">
          {buying ? <><Loader2 size={14} className="animate-spin" /> <span>Settling</span></> :
            available === 0 ? <span>Fully Funded</span> :
            <><span>Acquire {shares} {shares === 1 ? 'Share' : 'Shares'}</span> <ArrowUpRight size={14} strokeWidth={1.5} /></>}
        </button>
      </div>
    </div>
  );
};
